import { initializeApp, getApps } from "firebase/app";
import { getAnalytics, isSupported as analyticsSupported, logEvent } from "firebase/analytics";
import {
  getMessaging,
  getToken,
  isSupported as messagingSupported,
  onMessage,
} from "firebase/messaging";
import {
  FIREBASE_FCM_TOKEN_KEY,
  firebaseConfig,
  firebaseVapidKey,
} from "./config";
import {
  getPatientNotificationPathOrDefault,
  getProviderNotificationPathOrDefault,
} from "../utils/notificationRoutes";
import { tokenManager } from "../Api/tokenManager";

let app = null;
let messaging = null;
let analytics = null;
let initPromise = null;

function getFirebaseApp() {
  if (app) return app;
  app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
  return app;
}

function liveRole() {
  const patient = tokenManager.getTokenForRole("patient");
  const doctor = tokenManager.getTokenForRole("doctor");
  const hospital = tokenManager.getTokenForRole("hospital");
  const active = tokenManager.getActiveProviderRole();
  if (active === "hospital" && hospital) return "hospital";
  if (active === "doctor" && doctor) return "doctor";
  if (patient) return "patient";
  if (hospital) return "hospital";
  if (doctor) return "doctor";
  return null;
}

function pathForPayload(data = {}) {
  const role = liveRole();
  const payload = data && typeof data === "object" ? data : {};
  if (role === "patient") return getPatientNotificationPathOrDefault(payload);
  if (role === "doctor") {
    return getProviderNotificationPathOrDefault(payload, {
      isHospital: false,
      dashboardPath: "/doctor-dashboard",
    });
  }
  if (role === "hospital") {
    return getProviderNotificationPathOrDefault(payload, {
      isHospital: true,
      dashboardPath: "/hospital-dashboard",
    });
  }
  return "/";
}

/**
 * Initialize Firebase App + Analytics + FCM for the website.
 * Safe to call multiple times. Returns { token } when push is available.
 */
export function initHealbidWebFirebase() {
  if (initPromise) return initPromise;
  initPromise = (async () => {
    try {
      getFirebaseApp();

      if (await analyticsSupported()) {
        analytics = getAnalytics(getFirebaseApp());
        logEvent(analytics, "app_open", { channel: "web" });
      }

      if (!(await messagingSupported())) {
        console.info("[HealBid FCM] Messaging not supported in this browser");
        return { token: null, messaging: null };
      }

      messaging = getMessaging(getFirebaseApp());

      // Foreground messages (tab focused): surface as browser Notification when possible.
      onMessage(messaging, (payload) => {
        const title =
          payload?.notification?.title ||
          payload?.data?.title ||
          "HealBid";
        const body =
          payload?.notification?.body ||
          payload?.data?.body ||
          "You have a new update";
        const data = payload?.data || {};
        try {
          window.dispatchEvent(
            new CustomEvent("healbid:web-fcm", { detail: { payload, data } })
          );
        } catch (_) {
          /* ignore */
        }
        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
          const n = new Notification(title, {
            body,
            icon: "/Assets/icon.png",
            data,
          });
          n.onclick = () => {
            window.focus();
            const path = pathForPayload(data);
            if (path) window.location.assign(path);
            n.close();
          };
        }
      });

      return { token: null, messaging };
    } catch (err) {
      console.warn("[HealBid FCM] init failed:", err?.message || err);
      return { token: null, messaging: null };
    }
  })();
  return initPromise;
}

/**
 * Request notification permission and obtain an FCM registration token.
 * Requires REACT_APP_FIREBASE_VAPID_KEY (Web Push certificate in Firebase Console).
 */
export async function enableHealbidWebPush() {
  await initHealbidWebFirebase();
  if (!messaging) return null;

  if (typeof Notification === "undefined") return null;
  let permission = Notification.permission;
  if (permission === "default") {
    permission = await Notification.requestPermission();
  }
  if (permission !== "granted") {
    console.info("[HealBid FCM] Notification permission:", permission);
    return null;
  }

  if (!firebaseVapidKey) {
    console.warn(
      "[HealBid FCM] Missing REACT_APP_FIREBASE_VAPID_KEY. Generate a Web Push key pair in Firebase Console → Project settings → Cloud Messaging."
    );
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register(
      "/firebase-messaging-sw.js"
    );
    await navigator.serviceWorker.ready;

    const token = await getToken(messaging, {
      vapidKey: firebaseVapidKey,
      serviceWorkerRegistration: registration,
    });

    if (token) {
      try {
        localStorage.setItem(FIREBASE_FCM_TOKEN_KEY, token);
      } catch (_) {
        /* ignore */
      }
      console.info("[HealBid FCM] Web token ready");
    }
    return token || null;
  } catch (err) {
    console.warn("[HealBid FCM] getToken failed:", err?.message || err);
    return null;
  }
}

export function getStoredWebFcmToken() {
  try {
    return localStorage.getItem(FIREBASE_FCM_TOKEN_KEY) || "";
  } catch {
    return "";
  }
}

export function logWebAnalyticsEvent(name, params) {
  if (!analytics) return;
  try {
    logEvent(analytics, name, params);
  } catch (_) {
    /* ignore */
  }
}
